import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "./validate"

export type Comparison = {
  comparisonid: number
}

export type PropertyComparison = {
  assetid: number
  comparisonid: number
  sourcefolderid: number
  comparisonfolderid: number
  sourcepropertyid: number
  comparisonpropertyid: number
  status: string
}

// private module logic //

const id: string = "comparison"
const initialState: ApiResult<Comparison[]> = { id, state: "init", value: [] }
const comparisonStream = new BehaviorSubject<ApiResult<Comparison[]>>(initialState)
const comparisonEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let comparisonMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

const current = () => comparisonStream.getValue()

function sortData(data: Comparison[]): Comparison[] {
  data.sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code")));
  return data;
}

function clear(): void {
  comparisonStream.next({ id, state: 'ok', value: [] })
  comparisonMap = {}
}

function get(comparisonMapId: number): Comparison {
  return comparisonMap[comparisonMapId]
}

function fetchComparisons(): Promise<Comparison[] | { message: string }> {
  clear()
  comparisonStream.next({ id, state: 'busy', value: comparisonStream.getValue().value })
  let url = `comparison`
  return Client.get(url)
  .then((records: Comparison[]) => {
    comparisonStream.next({ id, state: 'ok', value: sortData(records) })
    validationService.fetchAll('comparison')
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetch(comparisonid: number): Promise<Comparison | { message: string }> {
  let url = `comparison?comparisonid=eq.${comparisonid}`
  return Client.get(url)
  .then((comparison: Comparison) => {
    validationService.fetch('comparison', comparisonid)
    return comparison
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function fetchComparisonsByFolderId(folderid: number): Promise<Comparison[] | { message: string }> {
  clear()
  comparisonStream.next({ id, state: 'busy', value: comparisonStream.getValue().value })
  let url = `comparison?sourcefolderid=eq.${folderid}`
  return Client.get(url)
  .then((records: Comparison[]) => {
    comparisonStream.next({ id, state: 'ok', value: sortData(records) })
    validationService.fetchAll('comparison')
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function findPropertyComparison(comparisonid: number, assetid: number): Promise<PropertyComparison | { message: string }> {
  let url = `comparisons/property`
  return Client.post(url, {comparisonid, assetid})
  .then((propertyComparison: PropertyComparison) => {
    return propertyComparison
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function create(): Promise<Comparison | { message: string }> {
  const streamVal = comparisonStream.getValue().value
  comparisonStream.next({ id, state: 'busy', value: streamVal })

  const payload = {
    type: "Cash Flow"
  }

  return Client.post(`comparison`, payload)
    .then((comparison: Comparison) => {
      push_undo({
        undofunc: () => deleteOne(comparison.comparisonid),
        redofunc: () => create()
      })
      comparisonMap[comparison.comparisonid] = comparison
      // merge results unique by comparisonid
      const streamVal = comparisonStream.getValue().value
      comparisonStream.next({
        id,
        state: 'ok',
        value: _.unionBy([comparison], streamVal, 'comparisonid')
      })
      comparisonEventStream.next({ type: 'COMPARISON_CREATED', payload: comparison })
      validationService.fetchAll('comparison')
      return comparison
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      comparisonStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
}

function copy(comparisonid:number, code:string) {
  const streamVal = comparisonStream.getValue().value;
  comparisonStream.next({ id, state: 'busy', value: streamVal });

  const payload = {table: 'comparison', recordid: comparisonid, _synchronous: true, code: incrementUnique(code, "code", streamVal)};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((comparisons: Comparison[]) => {
      const newRecord = comparisons[0]
      push_undo({
        undofunc: () => deleteOne(newRecord.comparisonid),
        redofunc: () => copy(comparisonid, code)
      });
      comparisonMap[newRecord.comparisonid] = newRecord
      // merge results unique by comparisonid
      const streamVal = comparisonStream.getValue().value
      comparisonStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], streamVal, 'comparisonid')
      })
      comparisonEventStream.next({ type: 'COMPARISON_COPIED', payload: newRecord })
      validationService.fetchAll('comparison')
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      comparisonStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      return err
    })
}

async function deleteOne(
  comparisonid: number
): Promise<boolean | { message: string }> {
  const streamVal = comparisonStream.getValue().value

  const deleteFn = () => deleteOne(comparisonid)
  const refreshFn = () => fetchComparisons()

  // filter out the deleted comparisonid
  const value = _(streamVal)
    .filter((p) => p.comparisonid !== comparisonid)
    .value()
  value.forEach((p) => (comparisonMap[p.comparisonid] = p))

  comparisonStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(comparisonid).then(() =>
  Client.del(`comparison?comparisonid=eq.${comparisonid}`)
    .then((comparison: object) => {
      delete comparisonMap[comparison['comparisonId']]
      push_undo({
        undofunc: () => {
          Client.post('comparison', comparison)
          comparisonMap[comparison['comparisonId']] = comparison
          comparisonStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteOne(comparisonid)
      })
      validationService.fetchAll('comparison')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      comparisonStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchComparisons()
      return err
    })
  )
}

async function deleteMultiple(
  comparisonids: number[]
): Promise<boolean | { message: string }> {
  const streamVal = comparisonStream.getValue().value

  const deleteFn = () => deleteMultiple(comparisonids)
  const refreshFn = () => fetchComparisons()

  // filter out the deleted comparisonid
  const value = _(streamVal)
    .filter((p) => !comparisonids.includes(p.comparisonid))
    .value()
  value.forEach((p) => (comparisonMap[p.comparisonid] = p))

  comparisonStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(comparisonids).then(() =>
  Promise.all(_.map(comparisonids, comparisonid => Client.del(`comparison?comparisonid=eq.${comparisonid}`)
    .then((comparison: Comparison) => {
      delete comparisonMap[comparison['comparisonId']]
      push_undo({
        undofunc: () => {
          Client.post('comparison', comparison)
          comparisonMap[comparison.comparisonid] = comparison
          comparisonStream.next({ id, state: 'ok', value: streamVal })
        },
        redofunc: () => deleteMultiple(comparisonids)
      })
      validationService.fetchAll('comparison')
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      comparisonStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchComparisons()
      return err
    })
  ))).then(trues => true)
}

function patch(comparison: {
  comparisonid: number
}): Promise<Comparison | { message: string }> {
  const streamVal = comparisonStream.getValue().value
  const comparisonid: number = comparison.comparisonid
  const streamValComparison = _.find(streamVal, { comparisonid })
  const changeset = diff_obj(comparison, streamValComparison)
  if (_.isEmpty(changeset)) return new Promise(() => comparison)
  push_undo({
    undofunc: () => patch(streamValComparison),
    redofunc: () => patch(comparison)
  })

  // optimistic update
  comparisonStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(streamVal), _.findIndex(streamVal, { comparisonid }), comparison)
  })

  return _patches_.watch(comparisonid, Client.patch(`comparison?comparisonid=eq.${comparisonid}`, changeset)
    .then((comparison: {comparisonid: number}) => {
      comparisonMap[comparison.comparisonid] = comparison
      if ('code' in comparison) validationService.fetchAll('comparison')
      else validationService.fetch('comparison', comparisonid)

      return comparison
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      comparisonStream.next({ id, state: 'error', error: err, value: streamVal })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchComparisons()
      return err
    }))

}

// public module interface //

const comparisonService = {
  stream: () => comparisonStream.asObservable(),
  value: () => comparisonStream.getValue().value,
  eventStream: () => comparisonEventStream.asObservable(),
  clear,
  get,
  fetch,
  fetchComparisons,
  fetchComparisonsByFolderId,
  findPropertyComparison,
  create,
  copy,
  deleteOne,
  deleteMultiple,
  patch
}

export default comparisonService
